import IconButton from './ui/IconButton'
import clsx from 'clsx'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense } from 'react'

interface PaginationProps {
	currentPage: number
	totalPages: number
	onPageChange: (page: number) => void
	maxVisible?: number
	className?: string
	isNewsPage?: boolean
}

export default function Pagination({
	currentPage,
	totalPages,
	onPageChange,
	maxVisible = 5,
	className,
	isNewsPage = false,
}: PaginationProps) {
	const router = useRouter()
	const searchParams = useSearchParams()

	const handlePageChange = (page: number) => {
		if (isNewsPage) {
			const params = new URLSearchParams(searchParams.toString())

			if (page === 1) {
				params.delete('page')
			} else {
				params.set('page', page.toString())
			}

			router.push(`?${params.toString()}`, { scroll: false })
		}

		onPageChange(page)
	}

	const getPageNumbers = () => {
		const pages: (number | string)[] = []

		if (totalPages <= maxVisible) {
			// Show all pages if total is less than maxVisible
			return Array.from({ length: totalPages }, (_, i) => i + 1)
		}
		// Always add first page
		pages.push(1)

		const beforePage = currentPage - 1
		const afterPage = currentPage + 1

		// Add ellipsis if there are more than 2 pages before the current page
		if (beforePage > 2) {
			pages.push('...')
		}

		// Add the page before current if it exists and isn't the first page
		if (beforePage > 1) {
			pages.push(beforePage)
		}

		// dd current page if it's not the first or last page
		if (currentPage > 1 && currentPage < totalPages) {
			pages.push(currentPage)
		}

		// Add the page after current if it exists and isn't the last page
		if (afterPage < totalPages) {
			pages.push(afterPage)
		}

		// Add ellipsis before last page if needed
		if (afterPage < totalPages - 1) {
			pages.push('...')
		}

		// Always add last page if not already included
		if (totalPages !== 1) {
			pages.push(totalPages)
		}

		return pages
	}

	return (
		<div className={clsx('flex justify-between items-center gap-2', className)}>
			<IconButton
				onClick={() => handlePageChange(currentPage - 1)}
				disabled={currentPage === 1}
				icon="Arrow"
				className="rotate-180"
				variant="outline-accent"
				aria-label="Go to previous page"
			/>

			<div className="flex gap-1">
				{getPageNumbers().map((page, index) =>
					page === '...' ? (
						<span key={`ellipsis-${index}`} className="px-3 py-1 text-gray-500">
							{page}
						</span>
					) : (
						<button
							key={`page-${page}`}
							onClick={() => handlePageChange(page as number)}
							disabled={page === currentPage}
							className={`px-3 py-1 text-sm font-medium
									${page === currentPage ? ' text-blue' : 'text-blue-dark/50'}`}
						>
							{page}
						</button>
					)
				)}
			</div>

			<IconButton
				onClick={() => handlePageChange(currentPage + 1)}
				disabled={currentPage === totalPages}
				icon="Arrow"
				variant="outline-accent"
				aria-label="Go to next page"
			/>
		</div>
	)
}
